import { isDevelopOrSnapshot, isProduction } from 'vet-bones/utils';

const stardogFeatureSupport = {
  autoMapping: { minVersion: '8.1.0' },
  autoMappingMultipleTables: { minVersion: '8.2.0' },
  // https://github.com/stardog-union/stardog/pull/11180
  autoMappingMultipleTablesTogether: { minVersion: '8.2.1' },
  // https://github.com/stardog-union/stardog/pull/10993
  autoMappingTargetType: { minVersion: '8.2.0' },
  catalog: { minVersion: '8.1.0' },
  databaseListInfo: { minVersion: '8.2.0' },
  dataSourceDescriptionEndpoint: { minVersion: '8.0.0' },
  // After 9.2.0 Stardog will automatically refresh metadata for new data sources
  dataSourceAutomaticMetadataRefresh: { minVersion: '9.2.0' },
  dataSourceQueryOptions: { minVersion: '8.2.0' },
  dataSourceRelationshipSuggestions: { minVersion: '9.2.0' },
  labelSuggestions: { minVersion: '9.0.0' },
};

export type StardogFeature = keyof typeof stardogFeatureSupport;

export const STARDOG_FEATURE_SUPPORT: Record<
  StardogFeature,
  { minVersion?: string; maxVersion?: string }
> = stardogFeatureSupport;

export type IsSupported<TFeature extends string> =
  `is${Capitalize<TFeature>}Supported`;

export type StardogFeatureSupported = {
  [key in IsSupported<StardogFeature>]: boolean;
};

export const DEFAULT_STARDOG_FEATURES: StardogFeatureSupported = {
  isAutoMappingSupported: false,
  isAutoMappingMultipleTablesSupported: false,
  isAutoMappingMultipleTablesTogetherSupported: false,
  isAutoMappingTargetTypeSupported: false,
  isCatalogSupported: false,
  isDatabaseListInfoSupported: false,
  isDataSourceDescriptionEndpointSupported: false,
  isDataSourceAutomaticMetadataRefreshSupported: false,
  isDataSourceQueryOptionsSupported: false,
  isDataSourceRelationshipSuggestionsSupported: false,
  isLabelSuggestionsSupported: false,
};

export const isAppFeatureEnabled = isDevelopOrSnapshot();

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AppFeature {}

const DEFAULT_APP_FEATURES: AppFeature = {
  ...(isProduction() ? {} : window.appFeatures),
};

export type FeaturesState = {
  app: AppFeature;
  stardogByConnectionId: Record<string, StardogFeatureSupported>;
};

export const createInitialFeaturesState = (): FeaturesState => ({
  app: DEFAULT_APP_FEATURES,
  stardogByConnectionId: {},
});

declare global {
  interface Window {
    appFeatures?: Partial<AppFeature>;
  }
}
